// /app/frontend/src/hooks/useRecognition.js
// PATCH B: Recognition - Private user badges computed on-the-fly

import { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from "../api/client";
import { AuthContext } from '../App';

/**
 * Hook to fetch user's private recognition badges
 * 
 * @returns {Object} { badges, badgeCount, loading, error, refetch }
 */
export function useRecognitionBadges() {
  const { token } = useContext(AuthContext);
  const [badges, setBadges] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBadges = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.get(`/me/recognition`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBadges(res.data.badges || []);
      setBadgeCount(res.data.badge_count || 0);
      setError(null);
    } catch (err) {
      console.error('[useRecognitionBadges] Error:', err);
      setError(err.response?.data?.detail || 'Failed to fetch recognition badges');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  return { badges, badgeCount, loading, error, refetch: fetchBadges };
}

/**
 * Hook to fetch full recognition summary with progress toward unearned badges
 * 
 * @returns {Object} { badges, progress, stats, badgeCount, loading, error, refetch }
 */
export function useRecognitionSummary() {
  const { token } = useContext(AuthContext);
  const [badges, setBadges] = useState([]);
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState({});
  const [badgeCount, setBadgeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.get(`/me/recognition/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBadges(res.data.badges || []);
      setProgress(res.data.progress || []);
      setStats(res.data.stats || {});
      setBadgeCount(res.data.badge_count || 0);
      setError(null);
    } catch (err) {
      console.error('[useRecognitionSummary] Error:', err);
      setError(err.response?.data?.detail || 'Failed to fetch recognition summary');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { badges, progress, stats, badgeCount, loading, error, refetch: fetchSummary };
}
